import React, { useState, useEffect } from 'react';
import { scheduleService } from '../../services/scheduleService';

function ExecutionHistoryView({ scheduleId, scheduleName, onClose }) {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadHistory = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const historyData = await scheduleService.getExecutionHistory(scheduleId);
      setHistory(historyData);
    } catch (err) {
      console.error('Failed to load execution history:', err);
      setError(err.message || 'Failed to load execution history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId]);

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const formatAudioLength = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  return (
    // Modal overlay
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
    >
      {/* Modal content */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '900px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.5rem',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(135deg, var(--color-sky) 0%, var(--color-primary) 100%)',
            color: 'white',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
          }}
        >
          <div>
            <h2 style={{ margin: '0 0 0.25rem 0', fontSize: '1.5rem' }}>📊 Execution History</h2>
            <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>{scheduleName}</p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              fontSize: '1.5rem',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          {isLoading && (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#718096' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
              <div>Loading execution history...</div>
            </div>
          )}

          {error && (
            <div
              style={{
                padding: '1rem',
                background: '#fed7d7',
                border: '1px solid #fc8181',
                borderRadius: '8px',
                color: '#742a2a',
                marginBottom: '1rem'
              }}
            >
              <strong>Error:</strong> {error}
            </div>
          )}

          {!isLoading && !error && history.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#718096' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
              <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>No Execution History</div>
              <div style={{ fontSize: '0.9rem' }}>
                This schedule hasn't executed yet. Check back after the next run time.
              </div>
            </div>
          )}

          {!isLoading && !error && history.length > 0 && (
            <div>
              <div style={{ marginBottom: '1rem', color: '#718096', fontSize: '0.9rem' }}>
                Showing last {history.length} execution{history.length !== 1 ? 's' : ''}
              </div>

              {/* Execution list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {history.map((execution, index) => (
                  <div
                    key={index}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '1rem',
                      background: execution.status === 'success' ? '#f0fff4' : '#fff5f5',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                  >
                    {/* Status badge and timestamp */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '0.75rem'
                      }}
                    >
                      <span
                        style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          background: execution.status === 'success' ? '#c6f6d5' : '#fed7d7',
                          color: execution.status === 'success' ? '#22543d' : '#742a2a'
                        }}
                      >
                        {execution.status === 'success' ? '✅ Success' : '❌ Failed'}
                      </span>
                      <div style={{ fontSize: '0.85rem', color: '#718096' }}>
                        {formatDate(execution.timestamp)}
                      </div>
                    </div>

                    {/* Episode details (if successful) */}
                    {execution.status === 'success' && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontSize: '1rem', fontWeight: '600', color: '#2d3748', marginBottom: '0.5rem' }}>
                          {execution.episodeTitle || 'Episode Generated'}
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
                          {execution.articlesCount !== undefined && (
                            <div>
                              <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.25rem' }}>
                                📰 Articles
                              </div>
                              <div style={{ fontSize: '0.9rem', fontWeight: '500', color: '#2d3748' }}>
                                {execution.articlesCount}
                              </div>
                            </div>
                          )}
                          
                          {execution.audioLength !== undefined && (
                            <div>
                              <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.25rem' }}>
                                🎙️ Audio Length
                              </div>
                              <div style={{ fontSize: '0.9rem', fontWeight: '500', color: '#2d3748' }}>
                                {formatAudioLength(execution.audioLength)}
                              </div>
                            </div>
                          )}
                          
                          {execution.duration !== undefined && (
                            <div>
                              <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.25rem' }}>
                                ⏱️ Generation Time
                              </div>
                              <div style={{ fontSize: '0.9rem', fontWeight: '500', color: '#2d3748' }}>
                                {execution.duration.toFixed(1)}s
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Audio link */}
                        {execution.episodeId && (
                          <div style={{ marginTop: '0.75rem' }}>
                            <a
                              href={`/episodes/${execution.episodeId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-primary btn-sm"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                            >
                              🎧 Listen to Episode
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Error details (if failed) */}
                    {execution.status === 'failed' && execution.error && (
                      <div
                        style={{
                          padding: '0.75rem',
                          background: 'white',
                          border: '1px solid #fc8181',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          color: '#742a2a',
                          fontFamily: 'monospace'
                        }}
                      >
                        <strong>Error:</strong> {execution.error}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid #e2e8f0',
            background: '#f7fafc',
            borderBottomLeftRadius: '12px',
            borderBottomRightRadius: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ fontSize: '0.85rem', color: '#718096' }}>
            History is retained for 14 days
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1.5rem',
              borderRadius: '6px',
              border: '1px solid #cbd5e0',
              background: 'white',
              color: '#2d3748',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = '#edf2f7'}
            onMouseOut={(e) => e.target.style.background = 'white'}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExecutionHistoryView;
